<script setup lang="ts">
import { ref, reactive } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';
import { PasswordResetForm } from '../models/PasswordResetForm';
import BaseForm from '@/shared/components/BaseForm.vue';
import {
  validateStrongPassword,
  validateCognitoPassword,
  cognitoPasswordGuidance,
} from '@/utils/validators';
import commissioningApiClient from '@/http/commissioningApiClient';
import { comApi } from '@/http/api';
import ButtonPrimary from '@shared/components/button/ButtonPrimary.vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { usePasswordRecovery } from '@/shared/auth/usePasswordRecovery';
import { PasswordUpdateError, resolveHttpErrorMessage } from '@/http/http-error';
import { logout } from '@/shared/auth/session';
import { isCognitoEnabled, isNativeCognitoEnabled } from '@/shared/auth/cognitoFeatureFlag';
import { cognitoClient } from '@/shared/auth/cognitoClient';
import { AppContext } from '@/models/AppContext';

const router = useRouter();
const route = useRoute();
const recoveryAvailable = usePasswordRecovery();
const token = route.params.token as string;
if (!token) {
  router.push({ name: 'ComLogin' });
}

const formRef = ref();
const isSaving = ref(false);
const completed = ref(false);
const passwordError = ref<{ message: string; recoveryRequired: boolean } | null>(null);
const passwordResetForm = reactive<PasswordResetForm>(new PasswordResetForm());
const validationRules: Record<string, Rule[]> = {
  password: [
    {
      validator: isCognitoEnabled ? validateCognitoPassword : validateStrongPassword,
      trigger: ['blur', 'change'],
    },
  ],
  confirmPassword: [
    {
      validator: (_, value) => {
        if (value !== passwordResetForm.password) {
          return Promise.reject('Passwords do not match');
        }
        return Promise.resolve();
      },
      trigger: ['blur', 'change'],
    },
  ],
};

const handleSubmit = async () => {
  if (!recoveryAvailable.value) return;
  if (isSaving.value || completed.value || passwordError.value?.recoveryRequired) return;
  isSaving.value = true;

  try {
    try {
      await formRef.value?.validate();
    } catch {
      return;
    }
    passwordError.value = null;
    const sessionVersion = cognitoClient.sessionVersion();
    passwordResetForm.token = token;
    await commissioningApiClient.post(comApi.resetPassword, passwordResetForm.serialize());
    completed.value = true;
    passwordResetForm.password = '';
    passwordResetForm.confirmPassword = '';
    message.success('Password reset successfully');
    if (isNativeCognitoEnabled && sessionVersion !== cognitoClient.sessionVersion()) return;
    try {
      if (isCognitoEnabled) await logout(AppContext.ComApp);
      else await router.push({ name: 'ComLogin' });
    } catch {
      message.error('Your password was reset. Close this session and sign in again.');
    }
  } catch (error) {
    passwordError.value = {
      message: resolveHttpErrorMessage(error, 'Password reset could not be confirmed.'),
      recoveryRequired: error instanceof PasswordUpdateError && error.recoveryRequired,
    };
    if (passwordError.value.recoveryRequired) {
      passwordResetForm.password = '';
      passwordResetForm.confirmPassword = '';
    }
    message.error(passwordError.value.message);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <main class="content">
    <h3>Password Reset</h3>
    <p v-if="!recoveryAvailable" role="status">
      Please contact Allume support to restore access to your account.
      <RouterLink to="/login">Back to sign in</RouterLink>
    </p>
    <p v-else-if="completed" role="status">
      Your password was reset. <RouterLink :to="{ name: 'ComLogin' }">Back to sign in</RouterLink>
    </p>
    <p v-if="passwordError" role="alert">{{ passwordError.message }}</p>
    <RouterLink v-if="passwordError?.recoveryRequired" :to="{ name: 'ComLogin' }"
      >Back to sign in</RouterLink
    >
    <BaseForm
      v-if="recoveryAvailable && !completed && !passwordError?.recoveryRequired"
      class="password-reset-form"
      ref="formRef"
      :model="passwordResetForm"
      :rules="validationRules"
      @finish="handleSubmit"
      id="com-password-reset-form"
    >
      <a-form-item
        name="password"
        :rules="validationRules.password"
        :extra="
          isCognitoEnabled
            ? cognitoPasswordGuidance
            : 'Use 6 to 20 characters, including a letter and a number.'
        "
      >
        <template #label>
          <span class="form-label">New password</span>
        </template>
        <a-input-password
          v-model:value="passwordResetForm.password"
          size="large"
          data-tn="password-reset-password-input"
        />
      </a-form-item>
      <a-form-item name="confirmPassword" :rules="validationRules.confirmPassword">
        <template #label>
          <span class="form-label">Confirm password</span>
        </template>
        <a-input-password
          v-model:value="passwordResetForm.confirmPassword"
          size="large"
          data-tn="password-reset-confirm-password-input"
        />
      </a-form-item>
    </BaseForm>

    <div
      v-if="recoveryAvailable && !completed && !passwordError?.recoveryRequired"
      class="button-container"
    >
      <ButtonPrimary block @click="handleSubmit">Submit</ButtonPrimary>
    </div>
  </main>
</template>

<style lang="less" scoped>
@import '../../../assets/style/variables.less';

.form-label {
  color: @color-primary;
  font-weight: @font-weight-medium;
}

.button-container {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
}

:deep(.ant-form) {
  width: 100%;
}

:deep(.ant-input) {
  border-radius: @border-radius-medium !important;
  border-color: @color-primary !important;
}

:deep(.ant-input-affix-wrapper) {
  border-radius: @border-radius-medium !important;
  border-color: @color-primary !important;
}

:deep(.ant-form-item-explain-error) {
  margin-bottom: 10px;
}
</style>
